import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button, Table, Modal, Input, InputNumber, Tag, message, Typography, Space, Tooltip, Card, Alert } from 'antd'
import { PlusOutlined, DeleteOutlined, CopyOutlined, KeyOutlined, ExclamationCircleFilled } from '@ant-design/icons'

const { Text, Paragraph } = Typography
const { confirm } = Modal

export interface ApiToken {
  id: number
  name: string
  token_prefix: string
  active: boolean
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
  created_at: string
}

export interface NewToken {
  id: number
  name: string
  token: string
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

export default function ApiTokensManager({ tokens, newToken }: { tokens: ApiToken[]; newToken: NewToken | null }) {
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState('')
  const [expireDays, setExpireDays] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  // Show the one-time reveal modal when the server hands back a new token via flash.
  const [revealDismissed, setRevealDismissed] = useState(false)

  const handleCreate = () => {
    if (!name.trim()) {
      message.warning('请输入 Token 名称')
      return
    }
    setCreating(true)
    router.post(
      '/settings/api_tokens',
      { name: name.trim(), expires_in_days: expireDays },
      {
        preserveScroll: true,
        onSuccess: () => {
          setCreateOpen(false)
          setName('')
          setExpireDays(null)
          setRevealDismissed(false)
        },
        onFinish: () => setCreating(false),
      },
    )
  }

  const handleRevoke = (token: ApiToken) => {
    confirm({
      title: '确认撤销',
      icon: <ExclamationCircleFilled />,
      content: (
        <div>
          <p>确定要撤销 Token <strong>{token.name}</strong> 吗？</p>
          <p className="text-gray-500 text-sm mt-1">撤销后使用该 Token 的所有集成将立即失效，且无法恢复。</p>
        </div>
      ),
      okText: '撤销',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => router.delete(`/settings/api_tokens/${token.id}`, { preserveScroll: true }),
    })
  }

  const copyToken = (tokenStr: string) => {
    navigator.clipboard.writeText(tokenStr)
    message.success('已复制到剪贴板')
  }

  const columns = [
    {
      title: '名称', dataIndex: 'name', key: 'name',
      render: (n: string) => (<Space><KeyOutlined className="text-gray-400" /><Text strong>{n}</Text></Space>),
    },
    { title: 'Token', dataIndex: 'token_prefix', key: 'token_prefix', render: (p: string) => <Text code className="text-xs">{p}</Text> },
    {
      title: '状态', key: 'status',
      render: (_: unknown, r: ApiToken) => r.revoked_at ? <Tag color="red">已撤销</Tag> : !r.active ? <Tag color="orange">已过期</Tag> : <Tag color="green">活跃</Tag>,
    },
    { title: '最后使用', dataIndex: 'last_used_at', key: 'last_used_at', render: (d: string | null) => <Text type="secondary" className="text-sm">{d ? formatDate(d) : '从未使用'}</Text> },
    { title: '过期时间', dataIndex: 'expires_at', key: 'expires_at', render: (d: string | null) => <Text type="secondary" className="text-sm">{d ? formatDate(d) : '永不过期'}</Text> },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: (d: string) => <Text type="secondary" className="text-sm">{formatDate(d)}</Text> },
    {
      title: '操作', key: 'action',
      render: (_: unknown, r: ApiToken) => (
        <Tooltip title={r.active ? '撤销 Token' : '已失效'}>
          <Button type="text" danger icon={<DeleteOutlined />} disabled={!r.active} onClick={() => handleRevoke(r)} />
        </Tooltip>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium text-gray-900 m-0">API Token</h2>
          <p className="text-sm text-gray-500 mt-1 mb-0">用于 OpenClaw、CLI 或其他第三方集成的访问令牌</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>创建 Token</Button>
      </div>

      <Table columns={columns} dataSource={tokens} rowKey="id" pagination={false} locale={{ emptyText: '暂无 API Token' }} size="middle" />

      <Modal
        title="创建 API Token"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => { setCreateOpen(false); setName(''); setExpireDays(null) }}
        confirmLoading={creating}
        okText="创建"
        cancelText="取消"
      >
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">名称 <span className="text-red-500">*</span></label>
            <Input placeholder="例如：OpenClaw、CI/CD" value={name} onChange={(e) => setName(e.target.value)} onPressEnter={handleCreate} maxLength={50} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">过期天数</label>
            <InputNumber placeholder="留空则永不过期" value={expireDays} onChange={(v) => setExpireDays(v)} min={1} max={3650} className="w-full" addonAfter="天" />
            <p className="text-xs text-gray-400 mt-1">不填则永不过期，建议定期轮换</p>
          </div>
        </div>
      </Modal>

      <Modal
        title={<Space><KeyOutlined className="text-green-500" /><span>Token 创建成功</span></Space>}
        open={!!newToken && !revealDismissed}
        onOk={() => setRevealDismissed(true)}
        onCancel={() => setRevealDismissed(true)}
        okText="我已保存"
        cancelButtonProps={{ style: { display: 'none' } }}
        closable={false}
        maskClosable={false}
      >
        {newToken && (
          <div className="space-y-4 py-2">
            <Alert type="warning" showIcon message="请立即复制并保存此 Token" description="关闭此窗口后将无法再次查看完整 Token" />
            <Card size="small" className="bg-gray-50">
              <div className="flex items-center justify-between gap-2">
                <Paragraph className="m-0 flex-1 break-all font-mono text-sm">{newToken.token}</Paragraph>
                <Tooltip title="复制"><Button type="text" icon={<CopyOutlined />} onClick={() => copyToken(newToken.token)} /></Tooltip>
              </div>
            </Card>
            <div className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs font-mono">
              {/* The same token feeds several integrations under different names:
                  pomodoro-tracker's is an established contract (renaming it breaks
                  it); /developers teaches ASAPTODO_TOKEN for Codex and scripts; the
                  old worklog skill reads ASAPTODO_API_TOKEN and people who installed
                  it still use that. All three are listed so whichever guide someone
                  followed matches what they see here. */}
              <p className="text-gray-400 m-0 mb-1"># pomodoro-tracker（OpenClaw）：</p>
              <p className="m-0 mb-2">export POMODORO_API_TOKEN={newToken.token}</p>
              <p className="text-gray-400 m-0 mb-1"># Codex / 脚本（见 /developers）：</p>
              <p className="m-0 mb-2">export ASAPTODO_TOKEN={newToken.token}</p>
              <p className="text-gray-400 m-0 mb-1"># 旧的 worklog skill：</p>
              <p className="m-0">export ASAPTODO_API_TOKEN={newToken.token}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
